import React, { useEffect, useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

function getOptionalAuthHeaders() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (!sessionToken) return {};
  return { Authorization: `Bearer ${sessionToken}` };
}

function PlayerMoments() {
  const [moments, setMoments] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filters, setFilters] = useState({
    matchId: '',
    playerId: '',
    triggerEvent: '',
    includeDeleted: false,
    includeDisabled: false,
  });

  const [providers, setProviders] = useState([]);
  const [genAiOptionsLoading, setGenAiOptionsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');

  const page = Math.floor(offset / limit) + 1;
  const totalPages = total > 0 ? Math.ceil(total / limit) : 1;

  useEffect(() => {
    fetchMoments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, limit]);

  useEffect(() => {
    const fetchGenAIOptions = async () => {
      setGenAiOptionsLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/admin/genai/options`, {
          method: 'GET',
          credentials: 'include',
          headers: getOptionalAuthHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.data?.providers) {
          setProviders(Array.isArray(data.data.providers) ? data.data.providers : []);
        }
      } catch (err) {
        // Non-blocking
      } finally {
        setGenAiOptionsLoading(false);
      }
    };
    fetchGenAIOptions();
  }, []);

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (filters.matchId) params.append('matchId', filters.matchId);
    if (filters.playerId) params.append('playerId', filters.playerId);
    if (filters.triggerEvent) params.append('triggerEvent', filters.triggerEvent.trim());
    if (filters.includeDeleted) params.append('includeDeleted', 'true');
    if (filters.includeDisabled) params.append('includeDisabled', 'true');
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    return params.toString();
  };

  const fetchMoments = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getOptionalAuthHeaders(),
      };
      const query = buildQuery();
      const res = await fetch(`${BASE_URL}/admin/player-moments?${query}`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });
      if (res.status === 401 || res.status === 403) {
        setError('Unauthorized. Please log in again.');
        setMoments([]);
        setTotal(0);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.statusCode !== '10000') {
        setError(data.message || 'Failed to load player moments');
        setMoments([]);
        setTotal(0);
        return;
      }
      const list = data.data?.moments ?? [];
      setMoments(Array.isArray(list) ? list : []);
      setTotal(data.data?.total ?? list.length ?? 0);
    } catch (err) {
      setError(err?.message || 'Failed to load player moments');
      setMoments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleChange = (e) => {
    const { name, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleApplyFilters = () => {
    setOffset(0);
    fetchMoments();
  };

  const handleResetFilters = () => {
    setFilters({
      matchId: '',
      playerId: '',
      triggerEvent: '',
      includeDeleted: false,
      includeDisabled: false,
    });
    setOffset(0);
    fetchMoments();
  };

  const handleChangePage = (direction) => {
    if (direction === 'prev') {
      setOffset((prev) => Math.max(prev - limit, 0));
    } else if (direction === 'next') {
      const nextOffset = offset + limit;
      if (nextOffset < total) {
        setOffset(nextOffset);
      }
    }
  };

  const handleTriggerGeneration = async () => {
    const matchId = filters.matchId.trim();
    if (!matchId) {
      setError('Enter a Match ID to trigger generation.');
      return;
    }
    setError('');
    setSuccess('');
    setTriggerLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getOptionalAuthHeaders(),
      };
      const body = {};
      if (selectedProvider) body.provider = selectedProvider;
      if (selectedModel) body.model = selectedModel;
      const res = await fetch(`${BASE_URL}/admin/player-moments/trigger/${matchId}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.statusCode !== '10000') {
        setError(data.message || 'Failed to trigger generation');
        return;
      }
      const summary = data.data;
      setSuccess(
        `Trigger completed. Detected: ${summary?.detected ?? 0}, Generated: ${
          summary?.generated ?? 0
        }, Skipped: ${summary?.skipped ?? 0}`,
      );
      fetchMoments();
    } catch (err) {
      setError(err?.message || 'Failed to trigger generation');
    } finally {
      setTriggerLoading(false);
    }
  };

  const updateMomentStatus = (updatedMoment) => {
    setMoments((prev) =>
      prev.map((m) => (m.id === updatedMoment.id ? { ...m, ...updatedMoment } : m)),
    );
  };

  const handleMomentAction = async (id, action) => {
    setError('');
    setSuccess('');
    setActionLoadingId(id);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getOptionalAuthHeaders(),
      };
      const res = await fetch(`${BASE_URL}/admin/player-moments/${id}/${action}`, {
        method: 'PATCH',
        credentials: 'include',
        headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.statusCode !== '10000') {
        setError(data.message || 'Action failed');
        return;
      }
      const updated = data.data?.moment;
      if (updated) {
        updateMomentStatus(updated);
      }
      if (action === 'enable') setSuccess('Moment enabled');
      else if (action === 'disable') setSuccess('Moment disabled');
      else if (action === 'delete') setSuccess('Moment soft deleted');
      else if (action === 'undo-delete') setSuccess('Delete undone');
    } catch (err) {
      setError(err?.message || 'Action failed');
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-6">
                <h3>Player Moments</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={2.5}>
                      <TextField
                        label="Match ID"
                        name="matchId"
                        size="small"
                        fullWidth
                        value={filters.matchId}
                        onChange={handleFilterChange}
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                      <TextField
                        label="Player ID"
                        name="playerId"
                        size="small"
                        fullWidth
                        value={filters.playerId}
                        onChange={handleFilterChange}
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Trigger Event"
                        name="triggerEvent"
                        placeholder="e.g. Century, 3-wicket haul"
                        size="small"
                        fullWidth
                        value={filters.triggerEvent}
                        onChange={handleFilterChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={filters.includeDisabled}
                            onChange={handleToggleChange}
                            name="includeDisabled"
                            size="small"
                          />
                        }
                        label="Include disabled"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={filters.includeDeleted}
                            onChange={handleToggleChange}
                            name="includeDeleted"
                            size="small"
                          />
                        }
                        label="Include deleted"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleResetFilters}
                          disabled={loading}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleApplyFilters}
                          disabled={loading}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total moments: {total}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                      <TextField
                        label="Trigger Match ID"
                        size="small"
                        type="number"
                        value={filters.matchId}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, matchId: e.target.value }))
                        }
                        sx={{ width: 140 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 140 }} disabled={genAiOptionsLoading}>
                        <InputLabel>LLM Provider</InputLabel>
                        <Select
                          value={selectedProvider}
                          label="LLM Provider"
                          onChange={(e) => {
                            setSelectedProvider(e.target.value);
                            setSelectedModel('');
                          }}
                        >
                          <MenuItem value="">Default</MenuItem>
                          {providers.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 180 }} disabled={!selectedProvider || genAiOptionsLoading}>
                        <InputLabel>LLM Model</InputLabel>
                        <Select
                          value={selectedModel}
                          label="LLM Model"
                          onChange={(e) => setSelectedModel(e.target.value)}
                        >
                          <MenuItem value="">Default</MenuItem>
                          {(providers.find((p) => p.id === selectedProvider)?.models || []).map((mod) => (
                            <MenuItem key={mod.id} value={mod.id}>
                              {mod.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleTriggerGeneration}
                        disabled={triggerLoading}
                      >
                        {triggerLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          'Trigger Generation'
                        )}
                      </Button>
                    </Box>
                  </Box>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2 }}
                      onClose={() => {
                        setError('');
                      }}
                    >
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert
                      severity="success"
                      sx={{ mb: 2 }}
                      onClose={() => {
                        setSuccess('');
                      }}
                    >
                      {success}
                    </Alert>
                  )}

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Match ID</TableCell>
                            <TableCell>Player</TableCell>
                            <TableCell>Trigger</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Descriptors</TableCell>
                            <TableCell>LLM Model</TableCell>
                            <TableCell>LLM Input</TableCell>
                            <TableCell>LLM Output</TableCell>
                            <TableCell>Enabled</TableCell>
                            <TableCell>Deleted</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {moments.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={10} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No player moments found.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            moments.map((m) => {
                              const isDeleted = !!m.deleted_at;
                              return (
                                <TableRow key={m.id}>
                                  <TableCell>{m.id}</TableCell>
                                  <TableCell>{m.match_id}</TableCell>
                                  <TableCell>
                                    {m.player_name || m.player_id}
                                  </TableCell>
                                  <TableCell>{m.trigger_event}</TableCell>
                                  <TableCell>{m.title}</TableCell>
                                  <TableCell>
                                    {Array.isArray(m.descriptors) && m.descriptors.length > 0 ? (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {m.descriptors
                                          .sort(
                                            (a, b) =>
                                              (a.position ?? 0) - (b.position ?? 0),
                                          )
                                          .map((d) => (
                                            <Chip
                                              key={`${d.word}-${d.position}`}
                                              size="small"
                                              label={`${d.word} (${d.weight})`}
                                            />
                                          ))}
                                      </Box>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">
                                      {m.llm_model || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">
                                      {typeof m.llm_input_tokens === 'number'
                                        ? m.llm_input_tokens
                                        : '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">
                                      {typeof m.llm_output_tokens === 'number'
                                        ? m.llm_output_tokens
                                        : '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={m.is_enabled ? 'Yes' : 'No'}
                                      color={m.is_enabled ? 'success' : 'default'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={isDeleted ? 'Yes' : 'No'}
                                      color={isDeleted ? 'error' : 'default'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption" color="text.secondary">
                                      {m.created_at
                                        ? new Date(m.created_at).toLocaleString()
                                        : '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 0.5,
                                        justifyContent: 'flex-end',
                                        flexWrap: 'wrap',
                                      }}
                                    >
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={actionLoadingId === m.id || m.is_enabled}
                                        onClick={() =>
                                          handleMomentAction(m.id, 'enable')
                                        }
                                      >
                                        Enable
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={
                                          actionLoadingId === m.id || !m.is_enabled
                                        }
                                        onClick={() =>
                                          handleMomentAction(m.id, 'disable')
                                        }
                                      >
                                        Disable
                                      </Button>
                                      {!isDeleted ? (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          disabled={actionLoadingId === m.id}
                                          onClick={() =>
                                            handleMomentAction(m.id, 'delete')
                                          }
                                        >
                                          Delete
                                        </Button>
                                      ) : (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={actionLoadingId === m.id}
                                          onClick={() =>
                                            handleMomentAction(m.id, 'undo-delete')
                                          }
                                        >
                                          Undo Delete
                                        </Button>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Page {page} of {totalPages}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={offset === 0 || loading}
                        onClick={() => handleChangePage('prev')}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={offset + limit >= total || loading}
                        onClick={() => handleChangePage('next')}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default PlayerMoments;

